import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import { authService } from "../../services/auth.service";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { loginSchema } from "./validations/auth.schema";
import { getAuthErrorKey } from "../../utils/authErrors";
import {
  authCardClass,
  authInputClass,
  authInputErrorClass,
  authLabelClass,
  authLinkClass,
} from "./authTheme";

const AUTH_ERROR_KEYS: Record<string, string> = {
  "auth.invalidCredentials": "login.errors.invalid",
  "auth.accountLocked": "login.errors.locked",
  "auth.inactiveAccount": "login.errors.inactive",
};

const Login = () => {
  const { t } = useTranslation("auth");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    "/profile";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const onSubmit = (data: { email: string; password: string }) => {
    setError("");
    setIsLoading(true);

    authService
      .login(data.email, data.password)
      .then((response) => {
        login(response);
        navigate(from, { replace: true });
      })
      .catch((err) => {
        console.error(err);
        const errorKey = getAuthErrorKey(err);
        const i18nKey = AUTH_ERROR_KEYS[errorKey] || "login.errors.invalid";
        setError(t(i18nKey));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Layout title={t("login.title")}>
      <div className="flex min-h-[70vh] items-center justify-center">
        <div className={authCardClass}>
          <div className="text-center mb-8">
            <h2 className="text-h2 tracking-tight text-white">
              {t("login.title")}
            </h2>
            <p className="mt-3 text-body-sm text-gray-400">
              {from === "/checkout" && (
                <span className="block mb-2 text-neon-green">
                  請先登入帳戶，先可以結帳。
                </span>
              )}
              {t("login.or")}{" "}
              <Link
                to="/register"
                state={location.state}
                className={authLinkClass}
              >
                {t("login.createAccount")}
              </Link>
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-body-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="email-address" className={authLabelClass}>
                  {t("login.email")}
                </label>
                <input
                  id="email-address"
                  type="email"
                  autoComplete="email"
                  {...register("email")}
                  className={`${authInputClass} ${
                    errors.email ? authInputErrorClass : ""
                  }`}
                  placeholder={t("login.emailPlaceholder")}
                />
                {errors.email && (
                  <p className="text-red-400 text-caption mt-1">
                    {t(errors.email.message as string)}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="password" className={authLabelClass}>
                  {t("login.password")}
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  {...register("password")}
                  className={`${authInputClass} ${
                    errors.password ? authInputErrorClass : ""
                  }`}
                  placeholder={t("login.passwordPlaceholder")}
                />
                {errors.password && (
                  <p className="text-red-400 text-caption mt-1">
                    {t(errors.password.message as string)}
                  </p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              isLoading={isLoading}
              className="w-full bg-gradient-brand text-space hover:opacity-90"
            >
              {isLoading ? t("login.signingIn") : t("login.submit")}
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Login;
